import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, CircularProgress, Stack, Button,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Print as PrintIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { auth, db } from '../../firebase';
import { collection, getDocs, query, where, doc, getDoc, orderBy } from 'firebase/firestore';

const PRIMARY = "#0d9488";

function Studentattendance() {
  const [loading, setLoading] = useState(true);
  const [schoolId, setSchoolId] = useState(null);
  
  // View Modes
  const [viewMode, setViewMode] = useState("daily"); // 'daily' | 'monthly'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Master Data
  const [courses, setCourses] = useState([]);
  const [studentsMap, setStudentsMap] = useState({});
  const [admissions, setAdmissions] = useState([]);
  
  // Dynamic Data
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({
    pG: 0, pB: 0, pT: 0,
    aG: 0, aB: 0, aT: 0,
    tG: 0, tB: 0, tT: 0
  });

  // 1. Initial Load: Get School Context & Master Data
  useEffect(() => {
    const initData = async () => {
      try {
        if (!auth.currentUser) return;
        const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
        
        if (userSnap.exists()) {
          const sId = userSnap.data().schoolId;
          setSchoolId(sId);
          await fetchMasterData(sId);
        }
      } catch (err) {
        console.error("Initialization Error:", err);
      }
    };
    initData();
  }, []);

  const fetchMasterData = async (sId) => {
    try {
      const coursesSnap = await getDocs(query(collection(db, "courses"), where("schoolId", "==", sId), orderBy("sequence", "asc")));
      const coursesList = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCourses(coursesList);

      const studentsSnap = await getDocs(query(collection(db, "students"), where("schoolId", "==", sId)));
      const sMap = {};
      studentsSnap.forEach(d => { sMap[d.id] = d.data(); });
      setStudentsMap(sMap);

      const admissionSnap = await getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("status", "==", "Active")));
      const admList = admissionSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAdmissions(admList);

    } catch (err) {
      console.error("Error fetching master data:", err);
    } finally {
      setLoading(false);
    }
  };

  // 2. Fetch Attendance and Compute Report
  useEffect(() => {
    const generateReport = async () => {
      if (!schoolId || courses.length === 0) return;
      setLoading(true);

      try {
        let attQuery;
        
        if (viewMode === "daily") {
          attQuery = query(collection(db, "studentAttendance"), where("date", "==", selectedDate));
        } else {
          // Monthly view logic
          const startOfMonth = `${selectedMonth}-01`;
          const [year, month] = selectedMonth.split('-');
          const lastDay = new Date(year, month, 0).getDate();
          const endOfMonth = `${selectedMonth}-${lastDay}`;
          
          attQuery = query(
            collection(db, "studentAttendance"), 
            where("date", ">=", startOfMonth),
            where("date", "<=", endOfMonth)
          );
        }

        const attSnap = await getDocs(attQuery);

        // Aggregate attendance records per student
        const attendanceMap = {}; // { studentId: { Present: X, Absent: Y } }
        attSnap.forEach(d => {
          const data = d.data();
          if (data.studentId) {
            if (!attendanceMap[data.studentId]) {
              attendanceMap[data.studentId] = { Present: 0, Absent: 0 };
            }
            if (data.status === "Present") attendanceMap[data.studentId].Present++;
            else attendanceMap[data.studentId].Absent++;
          }
        });

        let grandTotals = { pG: 0, pB: 0, pT: 0, aG: 0, aB: 0, aT: 0, tG: 0, tB: 0, tT: 0 };
        const rowData = [];

        courses.forEach(course => {
          let classTotals = {
            presentG: 0, presentB: 0,
            absentG: 0, absentB: 0,
            totalG: 0, totalB: 0
          };

          const classAdmissions = admissions.filter(a => a.class === course.id || a.classId === course.id);

          classAdmissions.forEach(adm => {
            const student = studentsMap[adm.studentId];
            if (!student) return;

            const gender = (student.gender || "").toUpperCase();
            const isBoy = gender.startsWith("M"); 
            const isGirl = gender.startsWith("F");

            // Base Strength 
            if (isBoy) classTotals.totalB++;
            if (isGirl) classTotals.totalG++;

            const stuRecords = attendanceMap[adm.studentId] || { Present: 0, Absent: 0 };

            if (viewMode === "daily") {
              if (stuRecords.Present > 0) {
                if (isBoy) classTotals.presentB++;
                if (isGirl) classTotals.presentG++;
              } else {
                if (isBoy) classTotals.absentB++;
                if (isGirl) classTotals.absentG++;
              }
            } else {
              // Monthly: Add all accumulated present/absent days
              if (isBoy) {
                classTotals.presentB += stuRecords.Present;
                classTotals.absentB += stuRecords.Absent;
              }
              if (isGirl) {
                classTotals.presentG += stuRecords.Present;
                classTotals.absentG += stuRecords.Absent;
              }
            }
          });

          // Compile Row Stats
          const pT = classTotals.presentG + classTotals.presentB;
          const aT = classTotals.absentG + classTotals.absentB;
          const tT = classTotals.totalG + classTotals.totalB;

          rowData.push({
            className: course.name,
            ...classTotals,
            presentT: pT,
            absentT: aT,
            totalT: tT
          });

          // Grand Totals
          grandTotals.pG += classTotals.presentG;
          grandTotals.pB += classTotals.presentB;
          grandTotals.pT += pT;
          grandTotals.aG += classTotals.absentG;
          grandTotals.aB += classTotals.absentB;
          grandTotals.aT += aT;
          grandTotals.tG += classTotals.totalG;
          grandTotals.tB += classTotals.totalB;
          grandTotals.tT += tT;
        });

        setReportData(rowData);
        setTotals(grandTotals);

      } catch (error) {
        console.error("Error generating report:", error);
      } finally {
        setLoading(false);
      }
    };

    generateReport();
  }, [selectedDate, selectedMonth, viewMode, schoolId, courses, admissions, studentsMap]);


  const handlePrint = () => {
    window.print();
  };

  // Styled Table Components for Print & Display
  const TableHeaderCell = ({ children, rowSpan, colSpan, align = "center" }) => (
    <TableCell 
      rowSpan={rowSpan} colSpan={colSpan} align={align}
      sx={{ 
        fontWeight: '900', border: '1px solid #cbd5e1', 
        bgcolor: '#f8fafc', color: '#0f172a',
        textTransform: 'uppercase', fontSize: '0.8rem', py: 1.5,
        '@media print': { borderColor: '#000', bgcolor: '#f1f5f9', color: '#000' }
      }}
    >
      {children}
    </TableCell>
  );

  const TableDataCell = ({ children, align = "center", isBold = false }) => (
    <TableCell 
      align={align}
      sx={{ 
        border: '1px solid #e2e8f0', py: 1,
        fontWeight: isBold ? '800' : '500',
        fontSize: '0.9rem', color: isBold ? '#0f172a' : '#334155',
        '@media print': { borderColor: '#000', color: '#000' }
      }}
    >
      {children === 0 ? "-" : children} 
    </TableCell>
  );

  return (
    <Box p={1} sx={{ minHeight: "100vh", bgcolor: "#f1f5f9" }}>
      
      {/* --- PRINT CSS BLOCK --- */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            border: none !important; 
          }
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>

      {/* HEADER SECTION (Hidden on Print) */}
      <Paper className="no-print" elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", background: "linear-gradient(to right, #ffffff, #f8fafc)" }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight="900" color="#0f172a" sx={{ letterSpacing: '-0.5px' }}>Student Attendance Register</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight="500">Class-wise Male / Female Breakdown</Typography>
          </Box>
          
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ bgcolor: "#fff", boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
            >
              <ToggleButton value="daily" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}><TodayIcon sx={{ mr: 1, fontSize: 18 }} /> Daily</ToggleButton>
              {/* <ToggleButton value="monthly" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}><CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> Monthly</ToggleButton> */}
            </ToggleButtonGroup>

            {viewMode === "daily" ? (
              <TextField 
                type="date" size="small"
                value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ bgcolor: "#fff", width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            ) : (
              <TextField 
                type="month" size="small"
                value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ bgcolor: "#fff", width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}

            <Button
              variant="contained"
              onClick={handlePrint}
              startIcon={<PrintIcon />}
              sx={{ bgcolor: PRIMARY, textTransform: "none", fontWeight: "bold", borderRadius: 2, px: 3 }}
            >
              Print Register
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* PRINTABLE AREA */}
      <Box className="printable-area">
        
        {/* Print-Only Title Header */}
        <Box className="print-only" sx={{ display: 'none', mb: 3, textAlign: 'center', '@media print': { display: 'block' } }}>
          <Typography variant="h5" fontWeight="bold">Student Attendance Register</Typography>
          <Typography variant="subtitle1">
            {viewMode === "daily" ? `Date: ${selectedDate}` : `Month: ${selectedMonth}`}
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", border: '1px solid #cbd5e1', bgcolor: '#fff', '@media print': { border: '2px solid #000', borderRadius: 0 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <CircularProgress sx={{ color: PRIMARY }} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
                
                {/* TABLE HEAD */}
                <TableHead>
                  <TableRow>
                    <TableHeaderCell rowSpan={2} align="left">STD (Class)</TableHeaderCell>
                    <TableHeaderCell colSpan={3}>No. of Present {viewMode === 'monthly' && '(Days)'}</TableHeaderCell>
                    <TableHeaderCell colSpan={3}>No. of Absent {viewMode === 'monthly' && '(Days)'}</TableHeaderCell>
                    <TableHeaderCell colSpan={3}>{viewMode === 'monthly' ? 'Base Strength' : 'Total'}</TableHeaderCell>
                    <TableHeaderCell rowSpan={2}>Tr. Sign</TableHeaderCell>
                  </TableRow>
                  <TableRow>
                    <TableHeaderCell>G</TableHeaderCell>
                    <TableHeaderCell>B</TableHeaderCell>
                    <TableHeaderCell>T</TableHeaderCell>

                    <TableHeaderCell>G</TableHeaderCell>
                    <TableHeaderCell>B</TableHeaderCell>
                    <TableHeaderCell>T</TableHeaderCell>

                    <TableHeaderCell>G</TableHeaderCell>
                    <TableHeaderCell>B</TableHeaderCell>
                    <TableHeaderCell>T</TableHeaderCell>
                  </TableRow>
                </TableHead>

                {/* TABLE BODY */}
                <TableBody>
                  {reportData.map((row, index) => (
                    <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { borderBottom: 'none' } }}>
                      <TableDataCell align="left" isBold>{row.className}</TableDataCell>
                      
                      {/* Present */}
                      <TableDataCell>{row.presentG}</TableDataCell>
                      <TableDataCell>{row.presentB}</TableDataCell>
                      <TableDataCell isBold>{row.presentT}</TableDataCell>

                      {/* Absent */}
                      <TableDataCell>{row.absentG}</TableDataCell>
                      <TableDataCell>{row.absentB}</TableDataCell>
                      <TableDataCell isBold>{row.absentT}</TableDataCell>

                      {/* Total */}
                      <TableDataCell>{row.totalG}</TableDataCell>
                      <TableDataCell>{row.totalB}</TableDataCell>
                      <TableDataCell isBold>{row.totalT}</TableDataCell>

                      <TableDataCell></TableDataCell> {/* Signature Column */}
                    </TableRow>
                  ))}

                  {/* TOTALS FOOTER ROW */}
                  <TableRow sx={{ bgcolor: '#fef2f2', '@media print': { bgcolor: '#f1f5f9' } }}>
                    <TableCell 
                      align="left" 
                      sx={{ border: '1px solid #e2e8f0', fontWeight: '900', color: '#b91c1c', py: 1.5, '@media print': { borderColor: '#000', color: '#000' } }}
                    >
                      GRAND TOTAL
                    </TableCell>
                    
                    {/* Grand Totals Present */}
                    <TableDataCell isBold>{totals.pG}</TableDataCell>
                    <TableDataCell isBold>{totals.pB}</TableDataCell>
                    <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: '900', color: '#b91c1c', fontSize: '1rem', '@media print': { borderColor: '#000', color: '#000' } }}>{totals.pT}</TableCell>

                    {/* Grand Totals Absent */}
                    <TableDataCell isBold>{totals.aG}</TableDataCell>
                    <TableDataCell isBold>{totals.aB}</TableDataCell>
                    <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: '900', color: '#b91c1c', fontSize: '1rem', '@media print': { borderColor: '#000', color: '#000' } }}>{totals.aT}</TableCell>

                    {/* Grand Totals Strength */}
                    <TableDataCell isBold>{totals.tG}</TableDataCell>
                    <TableDataCell isBold>{totals.tB}</TableDataCell>
                    <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: '900', color: '#b91c1c', fontSize: '1rem', '@media print': { borderColor: '#000', color: '#000' } }}>{totals.tT}</TableCell>
                    
                    <TableDataCell></TableDataCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default Studentattendance;